import { Building2, Users } from 'lucide-react';
import { useCompanyContext } from '../../contexts/CompanyContext';
import { useMultiCurrencyInfo } from '../../hooks/useMultiCurrencyInfo';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { motion, AnimatePresence } from 'motion/react';
import { CurrencyConversionBadge } from './CurrencyConversionBadge';

/**
 * SelectedCompaniesIndicator - Shows which companies are selected
 * 
 * Displays:
 * - Single company: Company name with entity type and currency
 * - Multiple companies: List of all selected companies
 * - Consolidated mode indicator with currency conversion badge
 * - Multi-currency badge when applicable
 * 
 * @example
 * ```tsx
 * <SelectedCompaniesIndicator />
 * ```
 */

export function SelectedCompaniesIndicator() {
  const { selectedCompanies, hasMultipleSelected } = useCompanyContext();
  const { hasMultipleCurrencies, baseCurrency, currencies, companiesByCurrency } = useMultiCurrencyInfo();

  if (selectedCompanies.length === 0) {
    return null;
  }

  const getEntityLabel = (type: 'personal' | 'business' | 'individual') => {
    switch (type) {
      case 'business':
        return 'Empresa';
      case 'personal':
        return 'Pessoal';
      case 'individual':
        return 'Individual';
      default:
        return '';
    }
  };

  // Single company - compact display
  if (!hasMultipleSelected) {
    const company = selectedCompanies[0];
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-2 text-muted-foreground"
      >
        <Building2 className="h-4 w-4" />
        <span>
          {company.name} • {getEntityLabel(company.entity_type)}
        </span>
      </motion.div>
    );
  }

  // Multiple companies - prominent display
  return (
    <Card className="p-4 bg-primary/5 border-primary/20">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary flex-shrink-0">
          <Users className="h-5 w-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3>Visão Consolidada</h3>
            <Badge variant="secondary">
              {selectedCompanies.length} empresas
            </Badge>
            
            {/* Badge de conversão de moeda quando aplicável */}
            {hasMultipleCurrencies && (
              <CurrencyConversionBadge
                baseCurrency={baseCurrency}
                currencies={currencies}
                companiesByCurrency={companiesByCurrency}
                variant="outline"
              />
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <AnimatePresence mode="popLayout">
              {selectedCompanies.map((company, index) => (
                <motion.div
                  key={company.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Badge variant="outline" className="gap-1.5">
                    <Building2 className="h-3 w-3" />
                    {company.name}
                  </Badge>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          <small className="text-muted-foreground mt-2 block">
            Os dados abaixo consolidam informações de todas as empresas selecionadas
            {hasMultipleCurrencies && ' com conversão automática de moedas'}
          </small>
        </div>
      </div>
    </Card>
  );
}
